/**
 * Client-side page bind for the Operations pane capability line.
 * Server resolveOperationsPageBind is authoritative for tools.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function crmTabFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/crm\/([^/?#]+)/i);
  if (match?.[1]) return match[1].toLowerCase();
  if (/^\/crm\/?$/i.test(pathname)) return 'dashboard';
  return null;
}

export function isCrmRoute(pathname: string): boolean {
  return /^\/crm(\/|$)/i.test(pathname);
}

export function isOutboundRoute(pathname: string): boolean {
  return /^\/outbound(\/|$)/i.test(pathname);
}

export function transformationIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/(?:tenant\/)?transformations\/([^/?#]+)/i);
  const id = match?.[1] || null;
  if (!id || id.toLowerCase() === 'create') return null;
  return UUID_RE.test(id) ? id : null;
}

export function isTransformationDetailRoute(pathname: string): boolean {
  return Boolean(transformationIdFromPath(pathname));
}

/** Pages that may show the Operations FAB / pane. */
export function isOperationsBoundPath(pathname: string): boolean {
  return isCrmRoute(pathname) || isOutboundRoute(pathname) || isTransformationDetailRoute(pathname);
}

export function operationsBindKey(pathname: string): string {
  const transformationId = transformationIdFromPath(pathname);
  if (transformationId) return `transformation:${transformationId}`;
  if (isOutboundRoute(pathname)) return 'crm-outbound';
  if (isCrmRoute(pathname)) return 'crm';
  return 'unbound';
}

export function operationsHonestyLine(pathname: string): string {
  return operationsHonestyCopy(pathname).lineFallback;
}

export interface OperationsHonestyCopy {
  boundAsKey: string;
  boundAsFallback: string;
  canKey: string;
  canFallback: string;
  cannotKey: string;
  cannotFallback: string;
  lineFallback: string;
}

export function operationsHonestyCopy(pathname: string): OperationsHonestyCopy {
  if (isTransformationDetailRoute(pathname)) {
    return {
      boundAsKey: 'operations.pane.boundAsProject',
      boundAsFallback: 'This project',
      canKey: 'operations.pane.honestyProjectCan',
      canFallback:
        'Add or update this project’s settings (dates, budget, classification, name), status (next steps and asks), inchstones, issues, risks, and actions. Apply a structure template or create inchstones with AI when you ask to replace the inchstone plan. Deletes confirm. The team shares this conversation. Undo a turn to reverse that write and everything after (last 100 writes).',
      cannotKey: 'operations.pane.honestyProjectCannot',
      cannotFallback:
        'CRM contacts or outreach. Other projects. Inventing notes, dates, owners, or extra settings. Applying a structure template unless you asked to replace the inchstone plan.',
      lineFallback:
        'This project: settings, status, inchstones, issues, risks, and actions. Undo is in this pane.',
    };
  }
  if (isOutboundRoute(pathname)) {
    return {
      boundAsKey: 'operations.pane.boundAsOutbound',
      boundAsFallback: 'Outbound',
      canKey: 'operations.pane.honestyOutboundCan',
      canFallback:
        'Log CRM records and move workflow stages. Deletes confirm. Undo is in this pane.',
      cannotKey: 'operations.pane.honestyOutboundCannot',
      cannotFallback: 'Send LinkedIn messages. Invent meeting notes. Change SMTP.',
      lineFallback: 'Outbound: log records and move stages. LinkedIn send stays on LinkedIn.',
    };
  }
  if (isCrmRoute(pathname)) {
    return {
      boundAsKey: 'operations.pane.boundAsCrm',
      boundAsFallback: 'This CRM page',
      canKey: 'operations.pane.honestyCrmCan',
      canFallback:
        'Find, add, update, or delete companies, contacts, interactions, campaigns, and email templates. Move workflow stages. Deletes confirm. Undo is in this pane.',
      cannotKey: 'operations.pane.honestyCrmCannot',
      cannotFallback: 'Change SMTP or dashboard widgets. Invent meeting notes. Send LinkedIn.',
      lineFallback: 'CRM: find, add, update, delete records. Deletes confirm. Undo is in this pane.',
    };
  }
  return {
    boundAsKey: 'operations.pane.boundAsUnbound',
    boundAsFallback: 'This page',
    canKey: 'operations.pane.honestyUnboundCan',
    canFallback: 'Use the controls on this page.',
    cannotKey: 'operations.pane.honestyUnboundCannot',
    cannotFallback:
      'This Operations pane is not bound here. Open CRM or a transformation project.',
    lineFallback:
      'This page is not bound yet. Open CRM or a transformation project, or use the controls on this page.',
  };
}
